'use client';

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/lib/auth';
import Link from 'next/link';

interface SavedItem {
  id: number;
  content_type: string;
  content_slug: string;
  notes: string | null;
  tags: string[];
  saved_at: string;
}

export default function SavedContentMenu() {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<SavedItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Fetch saved content when menu opens
   
  useEffect(() => {
    if (isOpen && isAuthenticated && items.length === 0) {
      setIsLoading(true);
      fetch('/api/secure/saved-content', {
        credentials: 'include'
      })
        .then(res => res.json())
        .then(data => {
          setItems(data.data.slice(0, 5)); // Show only 5 most recent
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Failed to load saved content:', err);
          setIsLoading(false);
        });
    }
  }, [isOpen, isAuthenticated, items.length]);
   

  const getContentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      insight: 'Insight',
      framework: 'Framework',
      comparison: 'Comparison',
      code: 'Code',
      architecture: 'Architecture',
      template: 'Template',
      'case-study': 'Case Study'
    };
    return labels[type] || type;
  };

  const getContentUrl = (type: string, slug: string) => {
    return `/${type === 'case-study' ? 'case-studies' : type}/${slug}`;
  };

  const formatTitle = (slug: string) => {
    return slug.split('-').map((word: string) =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg border border-border hover:border-border-muted0 hover:bg-accent-muted transition-colors relative"
        aria-label="Saved content"
        aria-expanded={isOpen}
      >
        <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
          />
        </svg>
        {items.length > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent text-accent-foreground text-xs font-bold rounded-full flex items-center justify-center">
            {items.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-surface border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Saved Content</h3>
          </div>

          {isLoading ? (
            <div className="px-4 py-8 text-center text-foreground-tertiary">
              <div className="animate-pulse">Loading...</div>
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <svg
                className="w-12 h-12 mx-auto mb-2 text-accent/30"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                />
              </svg>
              <p className="text-sm text-foreground-tertiary">No saved content yet</p>
              <p className="text-xs text-foreground-muted mt-1">
                Click the bookmark icon to save articles
              </p>
            </div>
          ) : (
            <>
              <div className="max-h-96 overflow-y-auto">
                {items.map((item) => (
                  <Link
                    key={item.id}
                    href={getContentUrl(item.content_type, item.content_slug)}
                    onClick={() => setIsOpen(false)}
                    className="block px-4 py-3 hover:bg-accent-muted transition-colors border-b border-border-muted last:border-b-0"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-xs uppercase tracking-wider text-accent font-medium">
                        {getContentTypeLabel(item.content_type)}
                      </span>
                      <span className="text-xs text-foreground-muted whitespace-nowrap">
                        {new Date(item.saved_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-foreground font-medium line-clamp-2">
                      {formatTitle(item.content_slug)}
                    </p>
                    {item.notes && (
                      <p className="text-xs text-foreground-tertiary mt-1 line-clamp-1">
                        {item.notes}
                      </p>
                    )}
                  </Link>
                ))}
              </div>

              <Link
                href="/dashboard/saved"
                onClick={() => setIsOpen(false)}
                className="block px-4 py-3 text-center text-sm font-medium text-accent hover:bg-accent-muted transition-colors border-t border-border"
              >
                View All Saved Content →
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
